import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Paperclip, FileText, FileCheck, Receipt as ReceiptIcon, AlertCircle,
  ChevronDown, ChevronUp, Inbox, Search, Filter, X as XIcon, Eye, EyeOff,
} from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';
import { EmailRecipients } from '../../components/admin/EmailRecipients';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  quote:      { label: 'Quote',     icon: FileCheck,   color: 'bg-blue-100 text-blue-800 border-blue-200' },
  invoice:    { label: 'Invoice',   icon: FileText,    color: 'bg-purple-100 text-purple-800 border-purple-200' },
  receipt:    { label: 'Receipt',   icon: ReceiptIcon, color: 'bg-green-100 text-green-800 border-green-200' },
  reminder:   { label: 'Reminder',  icon: AlertCircle, color: 'bg-amber-100 text-amber-800 border-amber-200' },
  travelsafe: { label: 'Readiness', icon: FileText,    color: 'bg-teal-100 text-teal-800 border-teal-200' },
  general:    { label: 'General',   icon: Mail,        color: 'bg-gray-100 text-gray-800 border-gray-200' },
};

const CATEGORY_ROUTE = {
  quote: 'quotes',
  invoice: 'invoices',
  receipt: 'receipts',
};

const formatTimestamp = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return formatDate(iso);
  }
};

// "3m ago", "2h ago", "yesterday", "3d ago" — friendly relative time for opens
const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return 'just now';
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'yesterday';
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
};

export const EmailActivity = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');

  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  // Filters
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState('all');
  const [openedFilter, setOpenedFilter] = useState('all'); // all | opened | unopened
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [emailFilter, setEmailFilter] = useState('');

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (password) fetchEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, status, dateFrom, dateTo, search, emailFilter]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/emails/stats`, {
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (res.ok) setStats(await res.json());
    } catch (err) { logger.error('stats fetch:', err); }
  };

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== 'all') params.append('category', category);
      if (status !== 'all') params.append('status', status);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (search.trim()) params.append('search', search.trim());
      if (emailFilter.trim()) params.append('email', emailFilter.trim());
      params.append('limit', '500');

      const res = await fetch(`${API_URL}/api/admin/emails?${params.toString()}`, {
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (res.ok) setEmails(await res.json());
      else if (res.status === 401) navigate('/admin/login');
    } catch (err) {
      logger.error('emails fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setCategory('all');
    setStatus('all');
    setOpenedFilter('all');
    setDateFrom('');
    setDateTo('');
    setSearch('');
    setEmailFilter('');
  };

  const hasActiveFilters = category !== 'all' || status !== 'all' || openedFilter !== 'all' || dateFrom || dateTo || search || emailFilter;

  const toggleExpand = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const openRelated = (email) => {
    const route = CATEGORY_ROUTE[email.related_type];
    if (route && email.related_id) navigate(`/admin/${route}/${email.related_id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
                <Mail className="w-6 h-6" />
                Email Activity
              </h1>
              <p className="text-sm text-foreground/60">
                Every outgoing email across every client — filter by date, status, category, or recipient.
              </p>
            </div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 transition-colors"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="email-stats-grid">
            <div className="bg-white border border-border rounded-xl p-4">
              <p className="text-xs text-foreground/60 uppercase tracking-wide">Total Sent</p>
              <p className="text-2xl font-bold text-foreground" data-testid="stats-total">{stats.total}</p>
            </div>
            <div className="bg-white border border-border rounded-xl p-4">
              <p className="text-xs text-foreground/60 uppercase tracking-wide">Last 7 Days</p>
              <p className="text-2xl font-bold text-primary">{stats.sent_7d}</p>
            </div>
            <div className="bg-white border border-border rounded-xl p-4">
              <p className="text-xs text-foreground/60 uppercase tracking-wide">Last 30 Days</p>
              <p className="text-2xl font-bold text-foreground">{stats.sent_30d}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="stats-opened-card">
              <p className="text-xs text-emerald-800/70 uppercase tracking-wide flex items-center gap-1"><Eye className="w-3 h-3" /> Opened</p>
              <p className="text-2xl font-bold text-emerald-700" data-testid="stats-opened">
                {stats.opened ?? 0}
                {typeof stats.open_rate === 'number' && (
                  <span className="text-sm font-semibold text-emerald-700/70 ml-1.5">· {Math.round(stats.open_rate * 100)}%</span>
                )}
              </p>
            </div>
            <div className={`rounded-xl p-4 border ${stats.failed > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-border'}`}>
              <p className="text-xs text-foreground/60 uppercase tracking-wide">Failed</p>
              <p className={`text-2xl font-bold ${stats.failed > 0 ? 'text-red-700' : 'text-foreground'}`} data-testid="stats-failed">
                {stats.failed}
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-border rounded-xl p-5 mb-6" data-testid="email-filters">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Filter className="w-4 h-4" /> Filters
            </h2>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                data-testid="reset-filters"
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <XIcon className="w-3 h-3" /> Reset
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-foreground/60 mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="filter-category"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All categories</option>
                {Object.entries(CATEGORY_META).map(([k, m]) => (
                  <option key={k} value={k}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-foreground/60 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="filter-status"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="partial">Partially sent</option>
                <option value="sending">Sending</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-foreground/60 mb-1">Opened</label>
              <select
                value={openedFilter}
                onChange={(e) => setOpenedFilter(e.target.value)}
                data-testid="filter-opened"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All</option>
                <option value="opened">Opened</option>
                <option value="unopened">Unopened</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-foreground/60 mb-1">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="filter-date-from"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs text-foreground/60 mb-1">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="filter-date-to"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-foreground/60 mb-1">Search subject</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="e.g. reminder, QT-2026"
                  data-testid="filter-search"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-foreground/60 mb-1">Recipient email</label>
              <input
                type="email"
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
                placeholder="client@example.com"
                data-testid="filter-email"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-foreground/60">Loading emails…</p>
          </div>
        ) : (() => {
          const visibleEmails = emails.filter((e) => {
            if (openedFilter === 'opened') return (e.open_count || 0) > 0;
            if (openedFilter === 'unopened') return !(e.open_count > 0);
            return true;
          });
          if (visibleEmails.length === 0) {
            return (
              <div className="bg-white border border-border rounded-xl p-12 text-center">
                <Inbox className="w-16 h-16 text-foreground/30 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-foreground mb-1">No emails match these filters</h2>
                <p className="text-sm text-foreground/60">
                  {hasActiveFilters ? 'Try adjusting the filters above.' : 'Emails sent from any quote, invoice, receipt, reminder, or readiness report will appear here.'}
                </p>
              </div>
            );
          }
          return (
          <>
            <p className="text-sm text-foreground/60 mb-3" data-testid="email-result-count">
              Showing {visibleEmails.length} email{visibleEmails.length === 1 ? '' : 's'}
            </p>
            <div className="space-y-3" data-testid="email-list">
              {visibleEmails.map((email) => {
                const meta = CATEGORY_META[email.category] || CATEGORY_META.general;
                const Icon = meta.icon;
                const isOpen = expanded[email.id];
                const isFailed = email.status === 'failed';
                const isSending = email.status === 'sending';
                const isPartial = email.status === 'partial';
                const opens = email.opens || [];
                const openCount = email.open_count || opens.length || 0;
                const wasOpened = openCount > 0;

                return (
                  <div
                    key={email.id}
                    data-testid={`email-card-${email.id}`}
                    className={`bg-white border rounded-xl overflow-hidden transition-all ${
                      isFailed ? 'border-red-200' : 'border-border'
                    }`}
                  >
                    <button
                      data-testid={`email-expand-${email.id}`}
                      onClick={() => toggleExpand(email.id)}
                      className="w-full px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <Icon className={`w-5 h-5 ${meta.color.split(' ')[1]}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${meta.color}`}>
                                {meta.label}
                              </span>
                              {email.related_number && (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => { e.stopPropagation(); openRelated(email); }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); openRelated(email); } }}
                                  className="font-mono text-xs text-primary hover:underline cursor-pointer"
                                >
                                  {email.related_number}
                                </span>
                              )}
                              {isFailed && (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800 border border-red-200">
                                  Failed
                                </span>
                              )}
                              {isSending && <span data-testid={`email-sending-${email.id}`} className="text-xs text-amber-800">Sending</span>}
                              {isPartial && <span data-testid={`email-partial-${email.id}`} className="text-xs font-medium text-amber-800">Partially sent</span>}
                              {!isFailed && !isSending && wasOpened && (
                                <span
                                  data-testid={`email-opened-badge-${email.id}`}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200"
                                  title={`First opened ${formatTimestamp(email.first_opened_at)}`}
                                >
                                  <Eye className="w-3 h-3" />
                                  Opened{openCount > 1 ? ` ${openCount}×` : ''} · {timeAgo(email.last_opened_at)}
                                </span>
                              )}
                              {!isFailed && !isSending && !isPartial && !wasOpened && (
                                <span
                                  data-testid={`email-unopened-badge-${email.id}`}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-foreground/60 border border-border"
                                  title="Recipient hasn't opened this email yet"
                                >
                                  <EyeOff className="w-3 h-3" />
                                  Unopened
                                </span>
                              )}
                              {email.attachments?.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-foreground/60">
                                  <Paperclip className="w-3 h-3" />
                                  {email.attachments.length}
                                </span>
                              )}
                            </div>
                            <p className="font-semibold text-foreground truncate">{email.subject}</p>
                            <p className="text-xs text-foreground/60 mt-0.5">
                              {formatTimestamp(email.sent_at)} ·{' '}
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => { e.stopPropagation(); navigate(`/admin/clients/${encodeURIComponent(email.to_email)}/emails`); }}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); navigate(`/admin/clients/${encodeURIComponent(email.to_email)}/emails`); } }}
                                className="text-primary hover:underline cursor-pointer"
                              >
                                {email.to_email}
                              </span>
                            </p>
                            <EmailRecipients email={email} testId={`email-recipients-${email.id}`} />
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-foreground/40">
                          {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t border-border bg-gray-50/50 p-5 space-y-4">
                        {email.attachments?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-foreground/60 uppercase tracking-wide mb-2">Attachments</p>
                            <div className="flex flex-wrap gap-2">
                              {email.attachments.map((name, idx) => (
                                <span
                                  key={`${email.id}-att-${idx}`}
                                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-border rounded-full text-xs font-medium text-foreground/80"
                                >
                                  <Paperclip className="w-3 h-3" />
                                  {name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {isFailed && email.error && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-xs font-semibold text-red-800 uppercase tracking-wide mb-1">Send failed</p>
                            <p className="text-sm text-red-700 font-mono">{email.error}</p>
                          </div>
                        )}
                        {!isFailed && (
                          <div data-testid={`email-opens-section-${email.id}`}>
                            <p className="text-xs font-semibold text-foreground/60 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                              <Eye className="w-3.5 h-3.5" /> Open activity
                            </p>
                            {opens.length === 0 ? (
                              <div className="bg-white border border-border rounded-lg p-3 text-xs text-foreground/60">
                                Not opened yet — we'll log the moment it happens.
                              </div>
                            ) : (
                              <div className="bg-white border border-border rounded-lg divide-y divide-border max-h-48 overflow-y-auto">
                                {opens.slice().reverse().map((o, idx) => (
                                  <div key={`${email.id}-open-${idx}`} className="px-3 py-2 flex items-center justify-between gap-3 text-xs">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <Eye className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                                      <span className="font-medium text-foreground truncate">{formatTimestamp(o.opened_at)}</span>
                                      <span className="text-foreground/50 truncate">· {timeAgo(o.opened_at)}</span>
                                    </div>
                                    {o.user_agent && (
                                      <span className="text-foreground/40 truncate max-w-[45%]" title={o.user_agent}>{o.user_agent}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        <div>
                          <p className="text-xs font-semibold text-foreground/60 uppercase tracking-wide mb-2">Message body</p>
                          <div
                            className="bg-white border border-border rounded-lg p-4 max-h-96 overflow-y-auto text-sm prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: email.body_html || '' }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
          );
        })()}
      </div>
    </div>
  );
};
